import moment from 'moment';
import { format } from 'date-fns';

/**
 * THIN-7647: "X / Y visits" primary label for the docs-due dashboard due-date column.
 * `visits` is the backend "completed/total" string (e.g. "2/5"), populated only for
 * progress-note rows. Returns '' when absent/malformed so callers fall back to the
 * plain due-date display used for non-progress-note documents.
 */
export const getVisitsLabel = (visits?: string): string => {
  if (!visits || !visits.includes('/')) {
    return '';
  }
  const [completed, total] = visits.split('/');
  return `${completed} / ${total} visits`;
};

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/**
 * THIN-7647: grayed sub-text for the docs-due dashboard due-date column. Pairs with the
 * "X / Y visits" primary line: a progress note is due either once the Yth visit is reached
 * OR on the day-based deadline (whichever the BE resolved as the earlier dueDate).
 *
 * THIN-8143: the line names the rule that produced the date. Since THIN-8142 the BE projects
 * the Yth visit from actual plus planned visits, so a due date with reason LAST_VISIT_SCHEDULED
 * is the visit rule, not the day rule, and "Or in 3 days" would mislabel it. Visit reasons read
 * "10th visit planned in 3 days (25 Sep, 2026)" / "10th visit completed today (…)"; day-rule and
 * unknown reasons keep "Or in 3 days (…)". Interim wording mirrors getDisplayString until the
 * design task (THIN-8146) lands final copy.
 */
export const getDueDateSubText = (dateString: any, alertReason?: string, visits?: string): string => {
  if (!dateString) {
    return '';
  }
  const dueDate = new Date(
    new Date(dateString).valueOf() + new Date(dateString).getTimezoneOffset() * 60 * 1000
  );
  const formattedDate = format(dueDate, 'dd MMM, yyyy');
  const daysToGo = moment(dateString).startOf('day').diff(moment().startOf('day'), 'days');
  const relative =
    daysToGo > 0
      ? `in ${daysToGo} ${daysToGo === 1 ? 'day' : 'days'}`
      : daysToGo === 0
        ? 'today'
        : `${Math.abs(daysToGo)} ${Math.abs(daysToGo) === 1 ? 'day' : 'days'} ago`;

  const totalVisits = parseInt(visits?.split('/')[1] ?? '', 10);
  if (!Number.isNaN(totalVisits)) {
    if (alertReason === 'LAST_VISIT_SCHEDULED') {
      return `${getOrdinal(totalVisits)} visit ${daysToGo < 0 ? 'was planned' : 'planned'} ${relative} (${formattedDate})`;
    }
    if (alertReason === 'LAST_VISIT_COMPLETED') {
      return `${getOrdinal(totalVisits)} visit completed${daysToGo > 0 ? '' : ` ${relative}`} (${formattedDate})`;
    }
  }

  return `Or ${relative} (${formattedDate})`;
};
export const getDisplayString = (
  dateString: any,
  fromRequiredDoc = false,
  doc?: any,
  maxProgressNoteDays?: any,
  source?: string,
  isDocDuePNAlert?: any
) => {
  if (!dateString) {
    return '-';
  }
  const currentDate = moment().startOf('day');
  const inputDate = moment(dateString).startOf('day');
  const diffInDays = currentDate.diff(inputDate, 'days');

  // weekly progress note alert
  if (isDocDuePNAlert && doc?.formType?.includes('WEEKLY_PROGRESS_NOTE')) {
    const [xStr, yStr] = doc?.visits?.split('/') || [];
    const x = parseInt(xStr, 10);
    const y = parseInt(yStr, 10);

    const isDueToday =
      source === 'RECHECK_ALERT'
        ? getStatus(dateString) === doc?.status
        : doc?.status === 'DUE TODAY';
    const isUpcoming =
      source === 'RECHECK_ALERT'
        ? getStatus(dateString) === doc?.status
        : doc?.status === 'UPCOMING DEADLINE';
    const isOverdue =
      source === 'RECHECK_ALERT' ? getStatus(dateString) === 'OVERDUE' : doc?.status === 'OVERDUE';

    const reason = doc?.alertReason;
    if (isDueToday && reason === 'LAST_VISIT_SCHEDULED') {
      const visitLabel = getOrdinal(y);
      return `${visitLabel} Visit Today`;
    }

    if (isUpcoming && reason === 'LAST_VISIT_SCHEDULED') {
      const visitLabel = getOrdinal(y);
      return `${visitLabel} Visit Scheduled`;
    }

    if (isDueToday && reason === 'LAST_VISIT_COMPLETED') {
      const visitLabel = getOrdinal(y);
      return `${visitLabel} Visit Completed`;
    }

    if (isDueToday && reason === 'MAX_DAY_REACHED') {
      const visitLabel = getOrdinal(maxProgressNoteDays);
      return `${visitLabel} Day Reached`;
    }

    if (isUpcoming && reason === 'ALERT_FOR_FUTURE') {
      const remainingDays =
        diffInDays && maxProgressNoteDays ? maxProgressNoteDays - Math.abs(diffInDays) : 0;
       if (remainingDays < 0) {
         return `In ${Math.abs(diffInDays)} Days`;
       }
      return `${remainingDays}/${maxProgressNoteDays} Days`;
    }

    if (isOverdue) {
      if (reason === 'LAST_VISIT_COMPLETED') {
        const visitLabel = getOrdinal(y);
        return `${visitLabel} Visit Completed`;
      }

      if (reason === 'MAX_DAY_REACHED') {
        const visitLabel = getOrdinal(maxProgressNoteDays);
        return `${visitLabel} Day Reached`;
      }
    }
  }

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays === -1) {
    return 'Tomorrow';
  } else {
    const absoluteDiffInDays = Math.abs(diffInDays);
    const daysString = absoluteDiffInDays > 1 ? `${absoluteDiffInDays} Days` : '1 Day';

    if (diffInDays > 0) {
      return `${daysString} Ago`;
    } else {
      if (fromRequiredDoc) {
        return `In ${daysString}`;
      } else {
        return `${daysString} To Go`;
      }
    }
  }
};

function getStatus(dueDateStr: string): 'DUE TODAY' | 'UPCOMING DEADLINE' | 'OVERDUE' {
  const getDateParts = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return { year, month, day };
  };

  const getTodayParts = (): { year: number; month: number; day: number } => {
    const now = new Date();
    return {
      year: now.getUTCFullYear(),
      month: now.getUTCMonth() + 1,
      day: now.getUTCDate(),
    };
  };

  const due = getDateParts(dueDateStr);
  const today = getTodayParts();

  const dueDateValue = new Date(Date.UTC(due.year, due.month - 1, due.day)).getTime();
  const todayValue = new Date(Date.UTC(today.year, today.month - 1, today.day)).getTime();

  if (todayValue === dueDateValue) return 'DUE TODAY';
  if (todayValue < dueDateValue) return 'UPCOMING DEADLINE';
  return 'OVERDUE';
}